//Dec15 src/contexts/FormBuilderWrapper.jsx:
//Feb2 FormBuilderWrapper.jsx:

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserManagementProvider } from './UserManagementContext';
import { FormThemeProvider } from './FormThemeContext';
import { FormLayoutProvider } from './FormLayoutContext';
import { FieldEditProvider } from './FieldEditContext';
import { FormSubmissionSettingsProvider } from './FormSubmissionSettingsContext';
import { FormActionsProvider } from './FormActionsContext';
import { FormAnswersProvider } from './FormAnswersContext';

import { FormProvider, useFormContext } from './FormContext'; // Include FormProvider (+ useFormContext for AUF gating)
import { FormSaveProvider } from './FormSaveContext'; // Include FormSaveProvider
import { FormPublishProvider } from './FormPublishContext'; // Include FormPublishProvider
import { FieldMetadataProvider } from './FieldMetadataContext'; // Import FieldMetadataProvider
import { MultiAnswerFieldProvider } from './MultiAnswerFieldContext'; // Import MultiAnswerFieldProvider
import { DropdownDependenciesProvider } from './DropdownDependenciesContext'; // Import DropdownDependenciesProvider
import { FormSubmissionsProvider } from './FormSubmissionsContext'; // Import FormSubmissionsProvider
import { FormListsDraftSyncProvider } from './FormListsDraftSyncContext';
import { OrgPromotionConfigProvider } from './OrgPromotionConfigContext';
import { OrgAssetConfigProvider } from './OrgAssetConfigContext';

import FormBuilder from '../components/FormSettings/FormBuilder';
import FormBuilderNavbarComponent from '../components/Navbar/FormBuilderNavbarComponent'; // Import the navbar
import RequireAccess from '../components/auth/RequireAccess';

// Template onboarding modal (shown once when entering from landing-page template flow)
import TemplateWelcomeModal from '../components/Dialogs/TemplateWelcomeModal';

// AUF restore success dialog (reads payload written by FormLayoutContext)
import AufRestoreSuccessDialog from '../AssetManager/AufRestoreSuccessDialog';
import { useAuth } from './AuthContext';
import { safeGetItem, safeRemoveItem } from '../utils/storageUtils';

function AUFEnabledWrapper({ children }) {
  const location = useLocation();
  const { formDetails } = useFormContext();
  // Route hint may be present on first open
  const routeAUF = location.state?.isAssetUploadForm === true;
  const docAUF   = formDetails?.isAssetUploadForm === true;
  // Sticky enable: once AUF is detected, keep it true for the session
  const [isAUFEnabled, setIsAUFEnabled] = React.useState(Boolean(routeAUF || docAUF));
  React.useEffect(() => {
    if (docAUF && !isAUFEnabled) setIsAUFEnabled(true);
    // do not flip back to false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docAUF]);
  return (
   <OrgPromotionConfigProvider enabled={isAUFEnabled}>
     <OrgAssetConfigProvider
       /* later: { bynder: true } when you wire providers */
       providerFlags={{}}
       /* table registry doesn’t need raw assetId by default */
       staticPolicy={{ includeAssetId: true }}
     >
       {children}
     </OrgAssetConfigProvider>
   </OrgPromotionConfigProvider>

  );
}

// ─────────────────────────────────────────────────────────────
// AUF Restore success dialog host
// - Always mounted inside the builder so it works on any sub-route
// - Reads org-scoped payload written by FormLayoutContext
// - Shows AufRestoreSuccessDialog and consumes the payload on close
// ─────────────────────────────────────────────────────────────
function AufRestoreSuccessDialogHost() {
  const location = useLocation();
  const { organizationId } = useAuth() || {};
  const { formId } = useFormContext();

  const successKey = React.useMemo(() => {
    return organizationId ? `org_${organizationId}_aufRestoreSuccess` : null;
  }, [organizationId]);

  const [open, setOpen] = React.useState(false);
  const [payload, setPayload] = React.useState(null);

  React.useEffect(() => {
    if (!successKey) return;
    if (!formId) return;

    const maybe = safeGetItem(successKey);

    if (!maybe || typeof maybe !== 'object') return;

    // Guard: only show if the payload is for THIS AUF
    if (maybe.aufId && maybe.aufId !== formId) {
      console.log('[AUF Restore Success Dialog Host] Payload aufId mismatch; ignoring.', {
        successKey,
        payloadAufId: maybe.aufId,
        activeFormId: formId,
      });
      return;
    }

    console.log('[AUF Restore Success Dialog Host] Opening dialog from payload:', {
      successKey,
      formId,
      label: maybe?.label || maybe?.fieldLabel || null,
      type: maybe?.type || null,
    });

    setPayload(maybe);
    setOpen(true);

    // NOTE: do NOT remove the payload here; we remove it onClose
    // so refresh still shows the dialog if they haven't seen it yet.
  }, [successKey, formId, location.key]);

  const handleClose = React.useCallback(() => {
    try {
      console.log('[AUF Restore Success Dialog Host] Closing dialog; consuming payload key:', successKey);
      if (successKey) safeRemoveItem(successKey);
    } catch (e) {
      console.warn('[AUF Restore Success Dialog Host] Failed to remove payload key (non-fatal):', e?.message || e);
    } finally {
      setOpen(false);
      setPayload(null);
    }
  }, [successKey]);

  return (
    <AufRestoreSuccessDialog
      open={open}
      payload={payload}
      onClose={handleClose}
      // Intentionally NOT passing onGoToPublish here.
      // Your UX is: restore → user adjusts field settings in layout editor → publish from navbar when ready.
    />
  );
}

/* ─────────────────────────────────────────────────────────────
   Template onboarding modal host
   - Shows ONLY when user enters builder from landing-page template flow
   - Shows ONCE per user + templateKey
   - Uses sessionStorage/localStorage directly (do NOT use storageUtils here)
   ───────────────────────────────────────────────────────────── */
function TemplateWelcomeModalHost() {
  const location = useLocation();
  const navigate = useNavigate();

  const { currentUser } = useAuth() || {};
  const { formId } = useFormContext();

  const [open, setOpen] = React.useState(false);
  const [templateLabel, setTemplateLabel] = React.useState('Your template');
  const [templateKey, setTemplateKey] = React.useState(null);

  // Read template onboarding intent (route state first, session fallback)
  React.useEffect(() => {
    try {
      if (!formId) return;

      const routeWantsOnboarding = location.state?.templateOnboarding === true;

      // Fallbacks from the signup flow (you already store these during Signup/SignupLoading)
      const ssTemplateKey = sessionStorage.getItem('signupTemplateKey') || null;
      const ssTemplateLabel = sessionStorage.getItem('templateLabel') || null;

      const routeTemplateKey = location.state?.templateKey || null;
      const routeTemplateLabel = location.state?.templateLabel || null;

      const resolvedKey = routeTemplateKey || ssTemplateKey || null;
      const resolvedLabel = routeTemplateLabel || ssTemplateLabel || 'Your template';

      setTemplateKey(resolvedKey);
      setTemplateLabel(resolvedLabel);

      // Only proceed if this session is actually from the landing template flow.
      // This prevents modal from showing for normal in-app duplication.
      if (!routeWantsOnboarding) return;

      // Need a user id to make "seen" sticky per-user
      const uid = currentUser?.uid || null;
      if (!uid) return;

      // Only show once per (uid + templateKey). If templateKey is missing, still show once per uid.
      const seenKey = resolvedKey
        ? `onboarding_seen_${uid}_${resolvedKey}`
        : `onboarding_seen_${uid}_generic`;

      const hasSeen = localStorage.getItem(seenKey) === '1';

      console.log('[TemplateWelcomeModalHost] onboarding gate-check:', {
        formId,
        uid,
        routeWantsOnboarding,
        resolvedKey,
        resolvedLabel,
        seenKey,
        hasSeen,
      });

      if (!hasSeen) {
        setOpen(true);
      }
    } catch (e) {
      console.warn('[TemplateWelcomeModalHost] Failed to evaluate onboarding state (non-fatal):', e?.message || e);
    }
  }, [formId, location.state, currentUser?.uid]);

  const handleClose = React.useCallback(() => {
    try {
      const uid = currentUser?.uid || null;
      if (!uid) {
        setOpen(false);
        return;
      }

      const seenKey = templateKey
        ? `onboarding_seen_${uid}_${templateKey}`
        : `onboarding_seen_${uid}_generic`;

      localStorage.setItem(seenKey, '1');

      console.log('[TemplateWelcomeModalHost] Marked onboarding as seen:', {
        seenKey,
        templateKey,
        templateLabel,
      });
    } catch (e) {
      console.warn('[TemplateWelcomeModalHost] Failed to persist seen flag (non-fatal):', e?.message || e);
    } finally {
      setOpen(false);
    }
  }, [currentUser?.uid, templateKey, templateLabel]);

  const handlePrimary = React.useCallback(() => {
    // For phase 1, the primary action is simply: close + let them start editing.
    // Later, we can add: open a guided drawer, or deep-link to a specific builder tab.
    handleClose();
  }, [handleClose]);

  return (
    <TemplateWelcomeModal
      open={open}
      onClose={handleClose}
      onPrimary={handlePrimary}
      templateLabel={templateLabel}
    />
  );
}

const FormBuilderWrapper = () => {
  const location = useLocation(); // Get the current location
  const isPreview = location.pathname.match(/\/form-builder\/[^/]+\/preview/); // Improved preview detection

  return (
  <UserManagementProvider>
    <FormProvider>
      <FormThemeProvider>
        <FieldMetadataProvider>
          <FormLayoutProvider>
            <FieldEditProvider>
              <MultiAnswerFieldProvider> 
               <FormAnswersProvider>
                <DropdownDependenciesProvider> 
                  <FormSubmissionSettingsProvider>
                    <FormActionsProvider>
                      <FormSubmissionsProvider>
                        <FormListsDraftSyncProvider>
                          <FormSaveProvider>
                            <FormPublishProvider>
                              <RequireAccess>
                                <AUFEnabledWrapper>
                                {/* Always mounted dialog host (reads storage & opens itself) */}
                                <AufRestoreSuccessDialogHost />

                                {/* Template onboarding (only when route state templateOnboarding === true) */}
                                <TemplateWelcomeModalHost />

                                <FormBuilderNavbarComponent />  {/* Always render it */}
                                <FormBuilder isPreview={isPreview} />
                                </AUFEnabledWrapper>
                              </RequireAccess>
                            </FormPublishProvider>
                          </FormSaveProvider>
                        </FormListsDraftSyncProvider>
                      </FormSubmissionsProvider>
                    </FormActionsProvider>
                  </FormSubmissionSettingsProvider>
                </DropdownDependenciesProvider>              
              </FormAnswersProvider>
              </MultiAnswerFieldProvider> 
            </FieldEditProvider>
          </FormLayoutProvider>
        </FieldMetadataProvider>
      </FormThemeProvider>
    </FormProvider>
  </UserManagementProvider>
  );
};

export default FormBuilderWrapper;
